import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { IBAN_COMPONENT_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex, Loader, Paper, Text, useMantineTheme } from '@mantine/core';
import React from 'react';
import { useBankAccountFormContext } from '../../BankAccountFormContext.tsx';
import { useBankAccountVerificationContext } from '../../BankAccountVerificationContext.tsx';
import { SummaryCard } from '@dealstack/frontend/shared/components/SummaryCard/SummaryCard.tsx';
import { resolveVerificationFailureCopy } from './RuleVerificationPanel.utils.ts';

// The check/verified/failed states for a rule-driven tab whose validationType has an
// external lookup. Unlike the IBAN fieldset there is no manual fallback to offer —
// the rule's fields already are the manual entry, so a failure keeps them editable
// and offers a retry instead.
export const RuleVerificationPanel = () => {
	const tCapManage = useTranslationCapManage();
	const theme = useMantineTheme();
	const { hasEditPermission } = useBankAccountFormContext();
	const verification = useBankAccountVerificationContext();
	const { status, result, failureKind, validationType } = verification;

	const failureCopy = failureKind
		? resolveVerificationFailureCopy(tCapManage, validationType, failureKind)
		: null;

	const summaryRows = result
		? [
				{
					label: tCapManage('bank_account.drawer.bank_name.label'),
					value: result.bankName,
				},
				{
					label: tCapManage('bank_account.drawer.account_number.label'),
					value: result.accountNumber,
				},
				{
					label: tCapManage('bank_account.drawer.bic.label'),
					value: result.bic,
				},
			].filter((row) => !!row.value)
		: [];

	return (
		<>
			{status === 'idle' && hasEditPermission && (
				<Button
					variant="transparent"
					w="fit-content"
					onClick={verification.onVerify}
					disabled={verification.verifyDisabled}
					data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_RULE_VERIFY_BUTTON}
				>
					{tCapManage('bank_account.drawer.check_account_button')}
				</Button>
			)}

			{status === 'loading' && (
				<Flex
					align="center"
					gap="0.5rem"
					data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_RULE_VERIFY_LOADING}
				>
					<Loader variant="oval" size="xs" />
					<Text size="sm" c="dimmed">
						{tCapManage('bank_account.drawer.iban_lookup_loading')}
					</Text>
				</Flex>
			)}

			{status === 'success' && result && (
				<SummaryCard
					title={tCapManage('bank_account.drawer.iban_lookup_success.title')}
					badgeLabel={tCapManage('bank_account.drawer.iban_lookup_success.badge')}
					rows={summaryRows}
					emptyText={tCapManage('bank_account.drawer.verification_success.no_details', {
						defaultValue:
							'Verified with the bank directory. It returned no further details.',
					})}
					highlighted
					dataTestId={IBAN_COMPONENT_ELEMENT_ID.BANK_RULE_VERIFY_SUMMARY}
				/>
			)}

			{status === 'error' && failureCopy && (
				<>
					<Paper
						withBorder
						p="0.5rem"
						data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_RULE_VERIFY_ERROR}
						style={{ borderColor: theme.colors.orange[2] }}
					>
						<Flex gap="1rem">
							<Icon
								variant="warning"
								size="xl"
								style={{ color: theme.colors.orange[6] }}
							/>
							<Flex direction="column" gap="0.25rem">
								<Text fw={600}>{failureCopy.title}</Text>
								<Text fw={400}>{failureCopy.text}</Text>
							</Flex>
						</Flex>
					</Paper>

					{hasEditPermission && (
						<Button
							variant="transparent"
							px={0}
							w="fit-content"
							onClick={verification.onVerify}
							disabled={verification.verifyDisabled}
							data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_RULE_VERIFY_BUTTON}
						>
							{tCapManage('bank_account.drawer.verification_failed.retry_button', {
								defaultValue: 'Check again',
							})}
						</Button>
					)}
				</>
			)}
		</>
	);
};
